import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, ShoppingCart, MessageSquareText, KeyRound, ExternalLink, ShieldCheck, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

function PhoneMock({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="mx-auto w-44 rounded-[22px] border border-[#D4AF37]/30 bg-[#0F0F14] p-2.5 shadow-[0_0_40px_rgba(212,175,55,0.08)]"
    >
      <div className="rounded-[14px] bg-[#070709] border border-[#D4AF37]/10 px-3 py-4 min-h-56 flex flex-col gap-3">
        {children}
      </div>
    </div>
  );
}

const STEPS = [
  {
    icon: Search,
    title: "打开淘宝，搜索卡密",
    desc: "打开手机淘宝 APP，在搜索框输入「合赢项目交流社区 卡密」，即可找到在售的卡密商品，也可点击本页底部的「打开淘宝搜索」按钮直达。",
    mock: (
      <>
        <div className="flex items-center gap-1.5 bg-white/95 rounded-full px-3 py-2">
          <Search className="w-3.5 h-3.5 text-orange-500" />
          <span className="text-[10px] text-gray-700">合赢项目交流社区 卡密</span>
        </div>
        <div className="text-[10px] text-gray-500 text-center">≈ 找到在售卡密商品</div>
        <div className="border border-[#D4AF37]/25 p-2 flex gap-2 items-center">
          <div className="w-10 h-10 bg-gradient-to-br from-[#F3E5AB] to-[#AA7C11] grid place-items-center text-[#070709] text-xs font-black">合</div>
          <div className="flex-1">
            <p className="text-[10px] text-gray-200 leading-tight">合赢社区 · 会员激活卡密</p>
            <p className="text-[10px] text-orange-400 font-bold mt-0.5">自动发货</p>
          </div>
        </div>
      </>
    ),
    testid: "guide-step-1",
  },
  {
    icon: ShoppingCart,
    title: "选择商品，下单付款",
    desc: "在搜索结果中选择「合赢项目交流社区」会员激活卡密商品，确认数量后下单付款。卡密为虚拟商品，下单前请核对商品标题与自动发货说明。",
    mock: (
      <>
        <div className="border border-[#D4AF37]/25 p-2">
          <div className="w-full h-14 bg-gradient-to-br from-[#1A1A24] to-[#0F0F14] grid place-items-center border border-[#D4AF37]/15">
            <KeyRound className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <p className="text-[10px] text-gray-200 mt-2 leading-tight">会员激活卡密 · 自动发货</p>
          <p className="text-[10px] text-gray-500 mt-0.5">月销 1000+ · 好评率 99%</p>
        </div>
        <div className="mt-auto bg-orange-500 rounded-full text-center py-1.5">
          <span className="text-[10px] text-white font-bold">立即购买</span>
        </div>
      </>
    ),
    testid: "guide-step-2",
  },
  {
    icon: MessageSquareText,
    title: "付款后自动发货，复制卡密",
    desc: "付款成功后系统自动发货：在「我的订单 → 订单详情」或旺旺聊天消息中查看 12 位卡密（格式 HY-XXXX-XXXX-XXXX），长按复制。",
    mock: (
      <>
        <p className="text-[10px] text-gray-500 text-center">旺旺 · 卖家消息</p>
        <div className="bg-white/95 rounded-lg rounded-tl-none px-2.5 py-2 max-w-[85%]">
          <p className="text-[10px] text-gray-700 leading-relaxed">亲，您的卡密已发货：</p>
          <p className="text-[11px] font-mono font-bold text-[#AA7C11] tracking-wider mt-1">HY-8K2M-9Q4X-77AB</p>
        </div>
        <div className="bg-[#D4AF37]/15 border border-[#D4AF37]/30 rounded-lg rounded-tr-none px-2.5 py-2 max-w-[70%] self-end">
          <p className="text-[10px] text-[#F3E5AB]">收到，谢谢！</p>
        </div>
      </>
    ),
    testid: "guide-step-3",
  },
  {
    icon: KeyRound,
    title: "回到本站，输入卡密激活",
    desc: "回到本站「个人中心」或「邀请大厅」，粘贴卡密点击「立即激活」，即刻解锁 ¥128/人邀请奖励、双群进度、USDT 提现与投稿资格。",
    mock: (
      <>
        <p className="text-[10px] text-gray-500 text-center tracking-widest">合赢 · 卡密激活</p>
        <div className="border border-[#D4AF37]/40 px-2.5 py-2 text-center">
          <span className="text-[11px] font-mono text-[#F3E5AB] tracking-widest">HY-8K2M-9Q4X-77AB</span>
        </div>
        <div className="bg-gradient-to-r from-[#F3E5AB] via-[#D4AF37] to-[#AA7C11] text-center py-1.5">
          <span className="text-[10px] text-[#070709] font-bold">立即激活</span>
        </div>
        <p className="text-[9px] text-emerald-400 text-center">✓ 激活成功 · 权益已解锁</p>
      </>
    ),
    testid: "guide-step-4",
  },
];

export default function CardGuide() {
  const [taobao, setTaobao] = useState("");

  useEffect(() => {
    api.get("/settings/public").then((r) => setTaobao(r.data?.taobao_url || "")).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen pt-28 pb-24 px-5 lg:px-8" data-testid="card-guide-page">
      <div className="mx-auto max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <Link to="/me" data-testid="guide-back-link" className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-[#D4AF37] transition-colors mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> 返回个人中心
          </Link>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#D4AF37] mb-4">CARD KEY GUIDE · 卡密获取教程</p>
          <h1 className="font-serif-sc text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
            四步获取卡密，<span className="text-gold-grad">即刻激活会员</span>
          </h1>
          <p className="text-gray-400 text-sm mb-14">打开淘宝搜索即可购买卡密，全程约 2 分钟，激活后解锁全部会员权益</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 mb-14">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 2) * 0.12 }}
              className="panel p-7 lg:p-8 flex flex-col sm:flex-row gap-6 items-start"
              data-testid={s.testid}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono-num text-3xl font-black text-gold-grad">{String(i + 1).padStart(2, "0")}</span>
                  <s.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif-sc text-lg font-bold mb-3">{s.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{s.desc}</p>
              </div>
              <PhoneMock testid={`${s.testid}-mock`}>{s.mock}</PhoneMock>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="panel p-8 lg:p-10 gold-glow text-center"
        >
          <ShieldCheck className="w-8 h-8 text-[#D4AF37] mx-auto mb-5" />
          <h3 className="font-serif-sc text-xl font-bold mb-3">准备好卡密了吗？</h3>
          <p className="text-sm text-gray-400 mb-8 leading-relaxed">
            卡密一卡一号，激活后立即绑定您的账号，请勿泄露给他人；在淘宝搜索「合赢项目交流社区」即可购买，付款前确认商品为自动发货卡密
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={taobao || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="guide-taobao-btn"
              className="btn-gold px-8 py-3 text-sm inline-flex items-center justify-center gap-2"
            >
              打开淘宝搜索卡密 <ExternalLink className="w-4 h-4" />
            </a>
            <Link to="/me" data-testid="guide-activate-btn" className="btn-ghost-gold px-8 py-3 text-sm inline-flex items-center justify-center gap-2">
              <KeyRound className="w-4 h-4" /> 已有卡密 · 去激活
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
